import type { Invoice, PayoutUserResponse } from 'payments-api/model';
import type { User } from 'payments-api/model/user';
import React, { useMemo, useState } from 'react';
import { makePath } from 'utils/routes';

import {
  LayoutRow,
  TextCell,
} from '@fragment/ui/src/components/Payments/LayoutRow';
import type { ColumnDefinition } from '@fragment/ui/src/components/Table/helpers';
import { NavTableV2 } from '@fragment/ui/src/components/Table/NavTableV2';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

// Helper to check if payout party matches a given externalId
const isPayoutPartyMatch = (
  payoutUser: PayoutUserResponse,
  externalId: string
): boolean => {
  if ('platform' in payoutUser && payoutUser.platform) {
    return false; // Platform payout doesn't match any user
  }
  return 'user_id' in payoutUser && payoutUser.user_id === externalId;
};

// Helper to get display string for payout party
const getPayoutPartyDisplay = (payoutUser: PayoutUserResponse): string => {
  if ('platform' in payoutUser && payoutUser.platform) {
    return 'Platform';
  }
  return 'user_id' in payoutUser ? payoutUser.user_id : 'Unknown';
};

type InvoiceRow = {
  id: string;
  created: string;
  totalForUser: bigint;
};

type ItemRow = {
  id: string;
  description: string;
  productId: string;
  payoutUser: string; // Display string: 'Platform' or user_id
  amount: string;
};

type UserViewProps = {
  user: User;
  userInvoices: Invoice[];
  externalId: string;
  workspaceId: string;
};

// Format amount as currency
const formatCurrency = (amount: string | bigint): string => {
  const amountNum =
    typeof amount === 'string' ? parseInt(amount, 10) : Number(amount);
  return `$${(amountNum / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

// Define schema for items table
const itemsSchema: readonly ColumnDefinition<ItemRow>[] = [
  {
    title: 'Description',
    format: (row: ItemRow) => row.description,
  },
  {
    title: 'Seller',
    format: (row: ItemRow) => (
      <span className="text-main-500">{row.payoutUser}</span>
    ),
  },
  {
    title: 'Amount',
    justify: 'right' as const,
    format: (row: ItemRow) => formatCurrency(row.amount),
  },
];

/**
 * UserView component - Shared logic and UI for displaying user information
 * Used by UserDetailPage
 */
export const UserView = ({
  user,
  userInvoices,
  externalId,
  workspaceId,
}: UserViewProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState<'invoices' | 'items'>('invoices');

  const tabOptions = useMemo(
    () => [
      { label: 'Invoices', value: 'invoices' as const },
      { label: 'Items', value: 'items' as const },
    ],
    []
  );

  // Prepare invoice data for SubTable
  const invoiceRows = useMemo(
    (): InvoiceRow[] =>
      userInvoices.map((invoice) => {
        // Check if this user is the buyer or involved as a seller
        const isBuyer = invoice.buyerUser === externalId;
        const userItemsInInvoice =
          invoice.lineItems?.filter(
            (item) =>
              isBuyer || isPayoutPartyMatch(item.payout_user, externalId)
          ) || [];

        const totalForUser = userItemsInInvoice.reduce(
          (sum, item) => sum + BigInt(item.amount),
          BigInt(0)
        );

        return {
          id: invoice.id,
          created: invoice.created,
          totalForUser,
        };
      }),
    [userInvoices, externalId]
  );

  // Prepare item data for SubTable
  const itemRows = useMemo(
    (): ItemRow[] =>
      userInvoices.flatMap((invoice) => {
        const isBuyer = invoice.buyerUser === externalId;
        return (invoice.lineItems || [])
          .filter(
            (item) =>
              isBuyer || isPayoutPartyMatch(item.payout_user, externalId)
          )
          .map((item) => ({
            id: item.id,
            description: item.description,
            productId: item.product_id,
            payoutUser: getPayoutPartyDisplay(item.payout_user),
            amount: item.amount,
          }));
      }),
    [userInvoices, externalId]
  );

  // Define schema for invoices table
  const invoicesSchema: readonly ColumnDefinition<InvoiceRow>[] = useMemo(
    () => [
      {
        title: 'Invoice',
        format: (row: InvoiceRow) => row.id,
      },
      {
        title: 'Date',
        format: (row: InvoiceRow) =>
          new Date(row.created).toLocaleDateString('en-US', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          }),
      },
      {
        title: 'Amount',
        justify: 'right' as const,
        format: (row: InvoiceRow) => formatCurrency(row.totalForUser),
      },
    ],
    []
  );

  const getInvoicePath = (invoice: InvoiceRow): string =>
    makePath(['w', workspaceId, 'payments', 'invoices', invoice.id], {
      absolute: true,
    });

  const getItemPath = (_item: ItemRow): string =>
    // Future: navigate to item detail or show item details
    // For now, return current path to make rows non-navigable
    '#';
  return (
    <>
      {/* User Name Header */}
      <LayoutRow height="small">
        <TextCell
          text="User"
          textSize="md"
          align="left"
          border="right"
          secondary
        />
        <TextCell
          text={user.externalId}
          textSize="md"
          align="left"
          border="none"
          fullWidth
        />
      </LayoutRow>

      {/* Tabs and Content */}
      <div className="pt-f4">
        <div className="flex flex-col w-full  min-h-f1 ">
          {/* Tabs */}
          <TabsV2
            options={tabOptions}
            selectedValue={activeTab}
            onClick={setActiveTab}
            data-testid="user-view-tabs"
          />

          <div>
            {/* Tab Content */}
            {activeTab === 'invoices' && (
              <NavTableV2
                data={invoiceRows}
                schema={invoicesSchema}
                getPath={getInvoicePath}
                textSize="md"
                showHeader={false}
                stickyHeader={false}
                emptyValue={{
                  Invoice: (
                    <span className="text-main-500">
                      No invoices found for this user
                    </span>
                  ),
                  Date: '',
                  Amount: '',
                }}
              />
            )}

            {activeTab === 'items' && (
              <NavTableV2
                data={itemRows}
                schema={itemsSchema}
                getPath={getItemPath}
                textSize="md"
                showHeader={false}
                stickyHeader={false}
                emptyValue={{
                  Description: (
                    <span className="text-main-500">
                      No items found for this user
                    </span>
                  ),
                  Seller: '',
                  Amount: '',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
};
